import { extendZodWithOpenApi } from "zod-openapi";
extendZodWithOpenApi(z);

import { ChecklistBlockSchema } from "block-system/blocks/Checklist/schema";
import {
  BlockBaseSchema,
  BlockIdPlaceholderSchema,
  ComponentBlockStyleSchema,
} from "block-system/types/base";
import { z } from "zod";
import { VerticalAlignmentSchema } from "../__shared__/components/AlignmentField/schema";
import { ButtonBlockSchema } from "../Button/schema";
import { ChatbotBlockSchema } from "../Chatbot/types";
import { EmbedBlockSchema } from "../Embed/schema";
import { FormBlockSchema } from "../Form/Form/schema";
import { KanbanBlockSchema } from "../Kanban/schema";
import { LinksBlockSchema } from "../Links/Links/schema";
import { MarkdownTextBlockSchema } from "../MarkdownText/schema";
import { MediaBlockSchema } from "../Media/schema";
import { StripePaymentBlockSchema } from "../StripePayment/schema";
import { TableBlockSchema } from "../Table/schema";
import { blockType } from "./constants";

export const MAX_COLUMN_COUNT = 3;
export const DEFAULT_ITEM_VERTICAL_ALIGNMENT = "center";

export const LayoutBlockConfigSchema = z.object({
  id: z.string().cuid().or(BlockIdPlaceholderSchema).optional(),
  title: z.string().optional(),
  columnCount: z
    .number()
    .int()
    .min(2)
    .max(MAX_COLUMN_COUNT)
    .default(MAX_COLUMN_COUNT),
  itemVerticalAlignments: z
    .array(VerticalAlignmentSchema)
    .max(3)
    .min(2)
    .default([
      DEFAULT_ITEM_VERTICAL_ALIGNMENT,
      DEFAULT_ITEM_VERTICAL_ALIGNMENT,
      DEFAULT_ITEM_VERTICAL_ALIGNMENT,
    ]),
  ordering: z
    .record(
      z
        .string()
        .cuid()
        .describe("Valid ID (cuid) of an already existing block."),
      z.number()
    )
    .describe(
      "The ordering of the children in the layout block. The key is the ID (cuid) of the child block, the value is the position of the child block in the layout block. Must include all the children of the block."
    ),
  style: ComponentBlockStyleSchema.default({
    alignment: "center",
    width: "wide",
  }),
});

export const LayoutAllowedItemSchema = z.discriminatedUnion("type", [
  TableBlockSchema,
  MediaBlockSchema,
  KanbanBlockSchema,
  MarkdownTextBlockSchema,
  EmbedBlockSchema,
  ChatbotBlockSchema,
  FormBlockSchema,
  LinksBlockSchema,
  ButtonBlockSchema,
  ChecklistBlockSchema,
  StripePaymentBlockSchema,
]);

type LayoutAllowedItemType = z.infer<typeof LayoutAllowedItemSchema>["type"];

const allowedItemTypeLiterals = [
  z.literal("table-listing-block"),
  z.literal("media-block"),
  z.literal("kanban-block"),
  z.literal("markdown-text-block"),
  z.literal("embed-block"),
  z.literal("chatbot-block"),
  z.literal("form-block"),
  z.literal("links-block"),
  z.literal("button-block"),
  z.literal("checklist-block"),
  z.literal("stripe-payment-block"),
] as const satisfies Array<z.ZodLiteral<LayoutAllowedItemType>>;

/**
 * Problem: Without any additional checks, the `allowedItemTypeLiterals` might contain only a subset of the
 * literals for types listed in `LayoutAllowedItemType` union.
 *
 * Solution: Verify via TypeScript that `allowedItemTypeLiterals` contains all the items in `LayoutAllowedItemType`
 */
type AllowedItemTypeLiteral = (typeof allowedItemTypeLiterals)[number];
type ContainsAllTheItems =
  LayoutAllowedItemType extends AllowedItemTypeLiteral["value"] ? true : false;

/**
 * If you see an error here, it means that `LayoutAllowedItemType` has a new item that is not listed in `allowedItemTypeLiterals`.
 */
// @ts-expect-error
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _verification: ContainsAllTheItems = false;

export const LayoutAllowedItemBlockTypeSchema = z.union(
  allowedItemTypeLiterals
);

export const LayoutAllowedItemsSchema = LayoutAllowedItemSchema.array()
  .max(3, "Maximum 3 items allowed")
  .default([])
  .describe(
    "Children of the Layout block. Any children must also be included in the ordering configuration."
  );

export type LayoutAllowedItem = z.infer<typeof LayoutAllowedItemSchema>;
export type LayoutAllowedItems = z.infer<typeof LayoutAllowedItemsSchema>;
export type LayoutAllowedItemTypes = LayoutAllowedItems[0]["type"];

export const LayoutBlockSchema = BlockBaseSchema.extend({
  type: z.literal(blockType),
  config: LayoutBlockConfigSchema,
  children: LayoutAllowedItemsSchema,
}).openapi({ ref: "LayoutBlock" });

export type LayoutItem = {
  name: string;
  block: LayoutAllowedItems[0] | null;
  index: number;
  order: number;
};
